import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Text, Flex, useToast, Skeleton, Switch, Button } from '@chakra-ui/react';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { GetParamsObject } from '@/model/query-params';
import { breadcrumb } from './breadcrumb';
import { CalendarEvent } from '@/model';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { useCalendarEvents, useDeleteCalendarEventsMutation, useGetCalendarEventsById } from '@/services/queries/calendar-events';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { CalendarEventsFilters } from './types';
import { CalendarEventsFilterBox } from './components/CalendarEventsFilterBox';
import { CalendarEventTable } from './components/CalendarEventsTable/CalendarEventsTable';
import { CalendarEventEditModal } from './components/CalendarEditModal';
import { CalendarEventCreateModal } from './components/CalendarCreateModal';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { ApplicationComponentId } from '@/model/application-component';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { useTranslation } from 'react-i18next';
import { useAllClub } from '@/services/queries/club';

export const ApplicationCalendarEvents = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [isAppAreaActive, setIsAppAreaActive] = useState(false);
  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(
    ApplicationComponentId.CALENDAR_EVENTS,
  );
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successKey = pendingAppAreaState ? 'area_toggled_enable' : 'area_toggled_disable';
    const errorKey = pendingAppAreaState ? 'area_toggle_failed_enable' : 'area_toggle_failed_disable';

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.CALENDAR_EVENTS, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({
          status: 'success',
          title: t(`pages.application_calendar_events.calendar_events_${successKey}`),
        });
      },
      onError: () => {
        toast({
          status: 'error',
          title: t(`pages.application_calendar_events.calendar_events_${errorKey}`),
        });

        setIsAppAreaActive(!pendingAppAreaState);
      },
    });
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [selectedItem, setSelectedItem] = useState<CalendarEvent | undefined>(undefined);

  const { data: clubs = [] } = useAllClub(true);
  const deleteMutation = useDeleteCalendarEventsMutation();
  const getItemByUUID = useGetCalendarEventsById();

  const filtersForm = useFiltersForm<CalendarEventsFilters>({
    defaultValues: {
      team_uuid: '',
      name: '',
      start_date: '',
      end_date: '',
    },
  });
  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'event_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: itemData, isLoading, forceRefetch } = useCalendarEvents(params);

  const itemsData = itemData?.data || [];

  const onHandleDelete = (item: CalendarEvent) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: async () => {
        await new Promise(resolve => setTimeout(resolve, 1000));

        toast({
          title: t('pages.application_calendar_events.calendar_events_delete_success'),
          status: 'success',
        });

        setSelectedItem(undefined);
        forceRefetch();
      },
      onError: (resp: any) => {
        setSelectedItem(undefined);
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_calendar_events.calendar_events_delete_failed'),
        });
      },
    });
  };

  const onHandleEdit = (uuid: string) => {
    getItemByUUID.mutate(uuid, {
      onSuccess: res => {
        setSelectedItem(res);
        setIsEditModalOpen(true);
      },
      onError: (resp: any) => {
        toast({
          status: 'error',
          title: (resp && resp.message) || t('pages.application_calendar_events.calendar_events_not_found'),
        });
      },
    });
  };

  const onHandleEditDone = useCallback(async () => {
    await new Promise(resolve => setTimeout(resolve, 1000));

    setIsEditModalOpen(false);
    setSelectedItem(undefined);

    forceRefetch();
  }, [forceRefetch]);

  const onHandleCreateDone = useCallback(() => {
    setIsCreateModalOpen(false);
    forceRefetch();
  }, [forceRefetch, setIsCreateModalOpen]);

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent title={t('pages.application_calendar_events.calendar_events_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
          <Flex alignItems='center' gap={4}>
            {isLoadingAppArea ? (
              <>
                <Skeleton w='130px' h='21px' />
                <Skeleton w='102px' h='21px' />
              </>
            ) : (
              <>
                <Text fontWeight='bold' color='brand.500'>
                  {t('pages.application_calendar_events.calendar_events_title_text')}
                </Text>
                <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                  {isAppAreaActive
                    ? t('pages.application_calendar_events.calendar_events_enabled')
                    : t('pages.application_calendar_events.calendar_events_disabled')}
                </Switch>
              </>
            )}
          </Flex>

          <Button
            display='flex'
            alignItems='center'
            gap={2}
            onClick={() => setIsCreateModalOpen(true)}
            width={{ base: '100%', md: 'initial' }}
          >
            <ChakraFeatherIcon name='plus-circle' />
            {t('pages.application_calendar_events.calendar_events_add_event')}
          </Button>
        </Flex>
        <SectionBox bodyProps={{ gap: 'small' }}>
          <CalendarEventsFilterBox clubs={clubs} filtersForm={filtersForm} forceRefetch={forceRefetch} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <CalendarEventTable
            data={itemsData}
            isLoading={isLoading}
            sorting={sorting}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalPages={itemData?.meta.pages || 0}
            totalItems={itemData?.meta.total || 0}
            onSortingChange={(field, desc) => setSorting({ field, desc })}
            onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
            onEditClick={item => onHandleEdit(item.uuid)}
            onDeleteClick={item => onHandleDelete(item)}
          />
        </Flex>
      </SectionBox>

      {selectedItem && (
        <CalendarEventEditModal
          isOpen={!!isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedItem(undefined);
          }}
          item={selectedItem}
          onDone={onHandleEditDone}
        />
      )}

      <CalendarEventCreateModal
        isOpen={!!isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onDone={onHandleCreateDone}
      />

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_calendar_events.calendar_events_delete_title')}
        description={
          <Text>{t('pages.application_calendar_events.calendar_events_delete_description', { name: selectedItem?.name })}</Text>
        }
      />

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_calendar_events.calendar_events_title_text')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_calendar_events.calendar_events_description_enabled')
              : t('pages.application_calendar_events.calendar_events_description_disabled')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={getItemByUUID.isLoading || isLoadingEnableOrDisableAppArea || deleteMutation.isLoading} />
    </PageContent>
  );
};
